// src/plan.ts
var ASSET_KINDS = [
  "thumbnail",
  "video_poster",
  "og_image",
  "segment_image",
  // 2026-06-28: per-section video (Issue #4). Routed through the
  // shared `uploadPitchAssetViaPresign` flow — same as the image
  // kinds — so the modal + bunny presign surface stays uniform.
  // The picked asset's $id is written into
  // `pitch_sections.bunnyVideoId` (schema column operator-pushed
  // per the standing `operator-pushes-schema` rule).
  "segment_video",
  // 2026-06-28 (Issue #2): pitch-level favicon + logo. Picked
  // from the Media step; written into `pitches.faviconFileId` +
  // `pitches.logoFileId` (schema columns operator-pushed per
  // the standing `operator-pushes-schema` rule).
  "favicon",
  "logo",
  // 2026-07-04: per-pitch share QR (share-widget QR). Cached on the
  // Bunny CDN at `pitch-media/{pitchId}/share-qr.png` (static path,
  // content-addressed via the `contentHash` column — same model as
  // og_image / thumbnail). Written by `pitch-og-generate`'s
  // `handleShareQrForPitch` on every publish; the warm-path join in
  // `pitches.js#fetchShareQrAssetsIndex` reads it back as the
  // `<img src>` of the share widget. Migration to enum form is
  // pinned by `plan.contract.test.ts` (ASSET_KINDS ↔
  // appwrite.config.json#pitch_assets[kind].elements).
  "share_qr",
  "attachment",
  "bunny_video"
];
var HIDDEN_SYSTEM_ASSET_KINDS = ["share_qr"];
function isHiddenSystemAssetKind(kind) {
  return HIDDEN_SYSTEM_ASSET_KINDS.includes(kind);
}
var KIND_LIMITS = {
  thumbnail: { maxBytes: 1 * 1024 * 1024, needsImageMime: true, needsVideoMime: false },
  video_poster: { maxBytes: 1 * 1024 * 1024, needsImageMime: true, needsVideoMime: false },
  og_image: { maxBytes: 2 * 1024 * 1024, needsImageMime: true, needsVideoMime: false },
  // segment_image: hero images attached to a single pitch section
  // (e.g. the `comic.png` above the hook_teaser on HP-5SMTL-5).
  // 5 MB cap leaves headroom for charts / OG-quality shots; the upload
  // script compresses source PNGs to WebP @ q=82 before upload.
  segment_image: { maxBytes: 5 * 1024 * 1024, needsImageMime: true, needsVideoMime: false },
  // 2026-06-28: per-section video (Issue #4). 100 MB cap leaves
  // headroom for typical 30-60s explainer clips at 1080p. Routes
  // through the existing `uploadPitchAssetViaPresign` flow (no
  // Bunny Stream detour).
  segment_video: { maxBytes: 100 * 1024 * 1024, needsImageMime: false, needsVideoMime: true },
  // 2026-06-28 (Issue #2): favicon + logo. 1 MB cap (favicons
  // are tiny; logos render small on the share card).
  favicon: { maxBytes: 1 * 1024 * 1024, needsImageMime: true, needsVideoMime: false },
  logo: { maxBytes: 5 * 1024 * 1024, needsImageMime: true, needsVideoMime: false },
  // 2026-07-04: share QR. Cached at 512×512 PNG, ~5-15 KB on disk.
  // 64 KB cap leaves 4× headroom for error-correction level variations
  // (H = 30% recovery bumps the matrix to 37×37 modules, ~14 KB).
  share_qr: { maxBytes: 64 * 1024, needsImageMime: true, needsVideoMime: false },
  attachment: { maxBytes: 100 * 1024 * 1024, needsImageMime: false, needsVideoMime: false },
  bunny_video: { maxBytes: 500 * 1024 * 1024, needsImageMime: false, needsVideoMime: true }
};
function getKindLimit(kind) {
  return KIND_LIMITS[kind];
}
function validateAssetUploadInput(input) {
  if (!input.pitchId || !input.pitchId.trim()) {
    return { ok: false, status: 400, error: "pitchId is required" };
  }
  if (!input.mimeType) {
    return { ok: false, status: 400, error: "mimeType is required" };
  }
  if (input.sizeBytes <= 0) {
    return { ok: false, status: 400, error: "sizeBytes must be > 0" };
  }
  if (!ASSET_KINDS.includes(input.kind)) {
    return {
      ok: false,
      status: 400,
      error: `kind must be one of: ${ASSET_KINDS.join(", ")} (got ${input.kind})`
    };
  }
  if (isHiddenSystemAssetKind(input.kind)) {
    return {
      ok: false,
      status: 400,
      error: `kind=${input.kind} is system-generated and cannot be uploaded directly`
    };
  }
  const limit = KIND_LIMITS[input.kind];
  if (input.sizeBytes > limit.maxBytes) {
    return {
      ok: false,
      status: 400,
      error: `kind=${input.kind} exceeds limit of ${formatBytes(limit.maxBytes)}`
    };
  }
  if (limit.needsImageMime && !input.mimeType.startsWith("image/")) {
    return {
      ok: false,
      status: 400,
      error: `kind=${input.kind} requires an image/* mime type (got ${input.mimeType})`
    };
  }
  if (limit.needsVideoMime && !input.mimeType.startsWith("video/")) {
    return {
      ok: false,
      status: 400,
      error: `kind=${input.kind} requires a video/* mime type (got ${input.mimeType})`
    };
  }
  return { ok: true };
}
function planAssetUpload(input, deps = { now: () => /* @__PURE__ */ new Date() }) {
  const v = validateAssetUploadInput(input);
  if (!v.ok) return v;
  const ts = (input.uploadedAt ?? deps.now()).toISOString();
  const stamp = ts.replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
  const ext = extensionFor(input.mimeType, input.kind);
  const kindSlug = input.kind.replace(/_/g, "-");
  const path = `pitch-media/${input.pitchId}/${kindSlug}-${stamp}.${ext}`;
  return {
    ok: true,
    path,
    recordPayload: {
      pitchId: input.pitchId,
      kind: input.kind,
      bunnyPath: path,
      // cdnUrl is a required attribute on the `pitch_assets` table. The
      // plan is intentionally CDN-agnostic (it doesn't know the runtime
      // BUNNY_CDN_BASE_URL), so it emits an empty string placeholder
      // that the handler overwrites via `buildCdnUrl(cdnBaseUrl, path)`
      // before calling writeAsset. Live incident 2026-06-11: this was
      // missing and the row insert failed with
      // `Missing required attribute "cdnUrl"`.
      cdnUrl: "",
      mimeType: input.mimeType,
      sizeBytes: input.sizeBytes,
      isPublic: input.isPublic,
      uploadedAt: ts
    }
  };
}
function buildCdnUrl(cdnBaseUrl, path) {
  if (!cdnBaseUrl) return "";
  const hasScheme = /^https?:\/\//i.test(cdnBaseUrl);
  const normalizedBase = (hasScheme ? cdnBaseUrl : `https://${cdnBaseUrl}`).replace(/\/+$/, "");
  const tail = path.replace(/^\/+/, "");
  return `${normalizedBase}/${tail}`;
}
function extensionFor(mime, kind) {
  if (kind === "bunny_video") return "mp4";
  const sub = mime.split("/")[1] ?? "bin";
  return sub.replace(/[^a-z0-9]/gi, "").toLowerCase() || "bin";
}
function formatBytes(n) {
  if (n >= 1024 * 1024) return `${Math.round(n / (1024 * 1024))}MB`;
  if (n >= 1024) return `${Math.round(n / 1024)}KB`;
  return `${n}B`;
}
var ASPECT_RATIOS = ["1:1", "1.91:1", "16:9", "4:3", "any"];
var SLOT_KINDS = [
  "og_image",
  "thumbnail",
  "segment_image",
  "video_poster",
  "favicon",
  "logo",
  "share_qr",
  "section_video",
  "bunny_video",
  "attachment"
];
var SLOT_REQUIRED_ASPECT = {
  og_image: "1.91:1",
  thumbnail: "16:9",
  segment_image: "any",
  video_poster: "16:9",
  favicon: "1:1",
  logo: "1:1",
  share_qr: "1:1",
  section_video: "any",
  bunny_video: "any",
  attachment: "any"
};
function isAssetCompatibleWithSlot(asset, slot) {
  if (Array.isArray(asset.slotCompat) && asset.slotCompat.length > 0) {
    return asset.slotCompat.includes(slot);
  }
  const required = SLOT_REQUIRED_ASPECT[slot];
  if (required === "any") return true;
  if (asset.aspectRatio === "any") return false;
  return asset.aspectRatio === required;
}
export {
  ASPECT_RATIOS,
  ASSET_KINDS,
  HIDDEN_SYSTEM_ASSET_KINDS,
  SLOT_KINDS,
  SLOT_REQUIRED_ASPECT,
  buildCdnUrl,
  getKindLimit,
  isAssetCompatibleWithSlot,
  isHiddenSystemAssetKind,
  planAssetUpload,
  validateAssetUploadInput
};
